import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Play, Users, Video, Eye, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { updateChannelSubscribers } from '../data/channels';
import { useYouTubeStats } from '../hooks/useYouTubeStats';

const ChannelsPage = () => {
  const { channelStats, loading } = useYouTubeStats();
  
  // Update channels with real API data
  const updatedChannels = updateChannelSubscribers(channelStats);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Our Channels - VR Media Network</title>
        <meta name="description" content="Explore all VR Media Network YouTube channels. From cooking and wellness to spirituality and storytelling, discover content that inspires and entertains." />
        <meta name="keywords" content="VR Media Network channels, YouTube channels, cooking, spirituality, wellness, storytelling" />
        <link rel="canonical" href="https://vrmedianetwork.com/channels" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Our Channels - VR Media Network" />
        <meta property="og:description" content="Explore all VR Media Network YouTube channels. From cooking and wellness to spirituality and storytelling." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vrmedianetwork.com/channels" />
        <meta property="og:image" content="https://vrmedianetwork.com/og-image.jpg" />
        <meta property="og:site_name" content="VR Media Network" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Channels - VR Media Network" />
        <meta name="twitter:description" content="Explore all VR Media Network YouTube channels. From cooking and wellness to spirituality and storytelling." />
        <meta name="twitter:image" content="https://vrmedianetwork.com/twitter-image.jpg" />
      </Helmet>

      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="pt-32 pb-16 bg-gradient-to-br from-vr-dark via-vr-darker to-vr-dark"
      >
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold font-display text-white mb-6">
            Our Channels
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Discover our diverse collection of YouTube channels, each offering unique content 
            tailored to different interests and passions. From spiritual guidance to culinary adventures, 
            we have something for everyone.
          </p>
        </div>
      </motion.section>

      {/* Channels Grid */}
      <section className="section-padding bg-vr-darker">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {updatedChannels.map((channel) => {
              const latestVideoData = channelStats[channel.name]?.latestVideo;
              return (
                <div key={channel.id} className="bg-vr-dark rounded-xl p-8 border border-gray-800">
                  {/* Channel Header */}
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center shadow-lg">
                      <img 
                        src={channel.logo} 
                        alt={`${channel.name} logo`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white text-lg font-bold" style={{display: 'none'}}>
                        {channel.name.split(' ').map(word => word[0]).join('')}
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2">{channel.name}</h2>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full bg-${channel.accentColor} text-white`}>
                          {channel.category}
                        </span>
                        <span className="text-gray-400 text-sm">
                          {loading ? 'Loading...' : (channelStats[channel.name]?.formattedSubscribers || channel.subscribers)} subscribers
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Channel Description */}
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {channel.description}
                  </p>

                  {/* Channel Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <Users className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <div className="text-sm text-gray-400">Subscribers</div>
                      <div className="text-lg font-semibold text-white">
                        {loading ? '...' : (channelStats[channel.name]?.formattedSubscribers || channel.subscribers)}
                      </div>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <Video className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <div className="text-sm text-gray-400">Videos</div>
                      <div className="text-lg font-semibold text-white">
                        {loading ? '...' : (channelStats[channel.name]?.videoCount || 'N/A')}
                      </div>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded-lg">
                      <Eye className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <div className="text-sm text-gray-400">Views</div>
                      <div className="text-lg font-semibold text-white">
                        {loading ? '...' : (channelStats[channel.name]?.viewCount ? `${(channelStats[channel.name].viewCount / 1000).toFixed(1)}K` : 'N/A')}
                      </div>
                    </div>
                  </div>

                  {/* Content Types */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-white mb-3">Content Includes:</h4>
                    <div className="flex flex-wrap gap-2">
                      {channel.content.map((item, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full border border-gray-700"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Latest Video */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-white mb-3">Latest Video:</h4>
                    {latestVideoData ? (
                      <div className="mb-3">
                        <h5 className="text-sm text-gray-300 mb-1 line-clamp-2">
                          {latestVideoData.title}
                        </h5>
                        <div className="flex items-center text-xs text-gray-400 mb-3">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(latestVideoData.publishedAt)}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 mb-3">Loading latest video...</p>
                    )}
                    <div className="relative aspect-video bg-gray-800 rounded-lg overflow-hidden">
                      {latestVideoData ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${latestVideoData.videoId}`}
                          title={`Latest video from ${channel.name}`}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                          <p className="text-gray-400">Video loading...</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subscribe Button */}
                  <a
                    href={channel.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center justify-center w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
                      channel.accentColor === 'vr-red' ? 'bg-vr-red hover:bg-red-600' :
                      channel.accentColor === 'vr-yellow' ? 'bg-vr-yellow hover:bg-yellow-500' :
                      channel.accentColor === 'vr-pink' ? 'bg-vr-pink hover:bg-pink-500' :
                      'bg-vr-blue hover:bg-blue-500'
                    }`}
                  >
                    <Play size={20} className="mr-2" />
                    Subscribe to {channel.name}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Channel Overview Table */}
      <section className="section-padding bg-vr-dark">
        <div className="container-custom">
          <h2 className="text-3xl font-bold font-display text-white text-center mb-8">
            Channel Overview
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-vr-darker rounded-lg overflow-hidden border border-gray-800">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Channel</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Category</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">Subscribers</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">Videos</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">Views</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {updatedChannels.map((channel, index) => (
                  <tr key={channel.id} className={index % 2 === 0 ? 'bg-vr-darker' : 'bg-gray-800/50'}>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center shadow-md">
                          <img 
                            src={channel.logo} 
                            alt={`${channel.name} logo`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full bg-gray-600 flex items-center justify-center text-white text-sm font-bold" style={{display: 'none'}}>
                            {channel.name.split(' ').map(word => word[0]).join('')}
                          </div>
                        </div>
                        <span className="text-white font-medium">{channel.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full bg-${channel.accentColor} text-white`}>
                        {channel.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-300">
                      {loading ? 'Loading...' : (channelStats[channel.name]?.formattedSubscribers || channel.subscribers)}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-300">
                      {loading ? 'Loading...' : (channelStats[channel.name]?.videoCount || 'N/A')}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-300">
                      {loading ? 'Loading...' : (channelStats[channel.name]?.viewCount ? `${(channelStats[channel.name].viewCount / 1000).toFixed(1)}K` : 'N/A')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <a
                        href={channel.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-white transition-colors ${
                          channel.accentColor === 'vr-red' ? 'bg-vr-red hover:bg-red-600' :
                          channel.accentColor === 'vr-yellow' ? 'bg-vr-yellow hover:bg-yellow-500' :
                          channel.accentColor === 'vr-pink' ? 'bg-vr-pink hover:bg-pink-500' :
                          'bg-vr-blue hover:bg-blue-500'
                        }`}
                      >
                        Subscribe
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-vr-red/20 via-vr-yellow/20 to-vr-blue/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
            Ready to Explore More?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Subscribe to all our channels and never miss out on the latest content from VR Media Network.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {updatedChannels.map((channel) => (
              <a
                key={channel.id}
                href={channel.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 transform hover:scale-105 ${
                  channel.accentColor === 'vr-red' ? 'bg-vr-red hover:bg-red-600' :
                  channel.accentColor === 'vr-yellow' ? 'bg-vr-yellow hover:bg-yellow-500' :
                  channel.accentColor === 'vr-pink' ? 'bg-vr-pink hover:bg-pink-500' :
                  'bg-vr-blue hover:bg-blue-500'
                }`}
              >
                <span>Subscribe to {channel.name}</span>
                <ArrowRight size={16} />
              </a>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ChannelsPage;
